import React, { useState, useRef, useEffect, useMemo } from "react";
import "./NewSession.css";
import ImageUploader from "react-images-upload";
import { resizeAndCompressImages, rotateAllImagesToVertical, cropImage } from "./utils/images-processing.tsx";
import { Button, Col, Container, Row } from "react-bootstrap";
import TargetScanner from "./components/TargetScanner.tsx";
import { TargetResultDto } from "../../types/TargetScannedDataDTOs.ts";
import { SessionReport } from "../../components/SessionReport.tsx";
import { backendURL, useApiFetch } from "../../utils/api.tsx";
import { useNotifications } from "../../contexts/NotificationContext.tsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, AuthContextValue } from "../../contexts/AuthContext.tsx";
import { getAllShootingEventsConfigs, getShootingEventConfig } from "../../configs/utils/ShootingEventConfigFactory.ts";
import { ShootingEventConfig } from "../../types/ShootingEventConfig.ts";
import { getInitialShootingEventId, rememberShootingEventId } from "../../configs/utils/ShootingEventSelectionStorage.ts";
import { useInfoDialog } from "../../contexts/InfoDialogContext.tsx";
import { useConfirmation } from "../../contexts/ConfirmationContext.tsx";
import { useUnsavedNavigation } from "../../contexts/UnsavedNavigationContext.tsx";
import helpTarget1 from "../../images/help/help_target1.jpg";
import helpTarget2 from "../../images/help/help_target2.JPG";
import helpTarget3 from "../../images/help/help_target3.jpeg";

type TargetImageUploaderProps = React.ComponentProps<typeof ImageUploader> & {
  defaultImages?: string[];
};

const TargetImageUploader = ImageUploader as React.ComponentType<TargetImageUploaderProps>;

interface NewSessionLocationState {
  competitionId?: string;
  competitionDate?: string;
  shootingEventId?: string;
  competitionName?: string;
}

interface AccountProfile {
  clubName?: string | null;
}

export function NewSession() {
  const apiFetch = useApiFetch();
  const { success } = useNotifications();
  const { showInfo } = useInfoDialog();
  const { confirm } = useConfirmation();
  const { registerNavigationGuard } = useUnsavedNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as NewSessionLocationState | null;
  const competitionMode = locationState !== null;
  const competitionId = competitionMode ? locationState.competitionId : undefined;
  const competitionDate = competitionMode ? locationState.competitionDate : undefined;
  const competitionShootingEventId = competitionMode ? locationState.shootingEventId : undefined;
  const competitionName = competitionMode ? locationState.competitionName : undefined;

  const authContext: AuthContextValue = useAuth();
  const shootingEventConfigs: ShootingEventConfig[] = useMemo(() => getAllShootingEventsConfigs(), []);
  const initialShootingEventId: string = getInitialShootingEventId(shootingEventConfigs, competitionShootingEventId);

  const [images, setImages] = useState<string[]>([]);
  const [compressedImages, setCompressedImages] = useState<string[]>([]);
  const [targetScannerRendered, setTargetScannerRendered] = useState<boolean>(false);
  const [imagesPickerVisible, setImagesPickerVisible] = useState<boolean>(true);
  const [sessionSaved, setSessionSaved] = useState<boolean>(false);
  const skipUnsavedSessionReportPromptRef = useRef<boolean>(false);
  const clubWasEditedRef = useRef<boolean>(false);

  const [selectedShootingEventId, setSelectedShootingEventId] = useState<string>(initialShootingEventId);
  const [shotsPerTarget, setShotsPerTarget] = useState<string>("10");
  const [testShotsPerTarget, setTestShotsPerTarget] = useState<string>("5");
  const [selectedCaliber, setSelectedCaliber] = useState<string>("");
  const [hitRadius, setHitRadius] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [owner, setOwner] = useState<string>("");
  const [club, setClub] = useState<string>("");

  const [targetResults, setTargetResults] = useState<Array<TargetResultDto>>([]);
  const hasUnsavedSessionReport: boolean = !sessionSaved && !targetScannerRendered && targetResults.length > 0;

  const shootingEventConfig: ShootingEventConfig = useMemo(
    () => getShootingEventConfig(selectedShootingEventId),
    [selectedShootingEventId]
  );

  useUnsavedSessionReportPrompt(hasUnsavedSessionReport, skipUnsavedSessionReportPromptRef, confirm, registerNavigationGuard);

  const SHOTS_PER_TARGET_LOCAL_STORAGE_KEY: string = shootingEventConfig.shootingEventId + "shotsPerTarget";
  const TEST_SHOTS_PER_TARGET_LOCAL_STORAGE_KEY: string = shootingEventConfig.shootingEventId + "testShotsPerTarget";

  useEffect(() => {
    const storedShotsPerTarget = localStorage.getItem(SHOTS_PER_TARGET_LOCAL_STORAGE_KEY);
    const storedTestShotsPerTarget = localStorage.getItem(TEST_SHOTS_PER_TARGET_LOCAL_STORAGE_KEY);

    setShotsPerTarget(storedShotsPerTarget || "10");
    setTestShotsPerTarget(storedTestShotsPerTarget || "5");
  }, [shootingEventConfig.shootingEventId]);

  useEffect(() => {
    if (competitionShootingEventId) {
      setSelectedShootingEventId(competitionShootingEventId);
    }
  }, [competitionShootingEventId]);

  useEffect(() => {
    if (competitionDate) {
      const parsedDate = new Date(competitionDate);
      if (!Number.isNaN(parsedDate.getTime())) {
        setSelectedDate(parsedDate.toLocaleDateString('en-CA'));
        return;
      }
    } else {
      setSelectedDate(new Date().toLocaleDateString('en-CA'));
    }

  }, [competitionDate])

  useEffect(() => {
    if (!competitionMode) {
      return;
    }

    let cancelled = false;

    const loadClubName = async () => {
      try {
        const account = await apiFetch<AccountProfile>(`${backendURL}/accounts/me`, {
          method: "GET",
          includeAuth: true,
          showErrorToast: false
        });

        if (!cancelled && !clubWasEditedRef.current && account.clubName?.trim()) {
          setClub(account.clubName);
        }
      } catch (err) {
        console.error("Failed to load account profile: ", err);
      }
    };

    loadClubName();

    return () => {
      cancelled = true;
    };
  }, [competitionMode]);

  useEffect(() => {
    setSelectedCaliber(shootingEventConfig.calibers[0].value.toString());
  }, [shootingEventConfig]);

  const handleShootingEventChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedShootingEventId = e.target.value;
    rememberShootingEventId(selectedShootingEventId);
    setSelectedShootingEventId(selectedShootingEventId);
    setTargetResults([]);
    setSessionSaved(false);
    skipUnsavedSessionReportPromptRef.current = false;
    setTargetScannerRendered(false);
    setImagesPickerVisible(true);
  };

  const onDrop = (_files: File[], pictures: string[]) => {
    setImages(pictures);
    setCompressedImages([]);

  };

  const processImagesBeforeScanning = async (imagesInBase64: string[]) => {
    if (shotsPerTarget == "") {
      window.alert("Liczba strzałów na tarczę musi być podana!");
      return;
    }

    if (competitionMode && (owner === "" || club === "")) {
      window.alert("Nazwisko i imię strzelca oraz klub muszą być podane!");
      return;
    }

    let processedImages = compressedImages;

    if (processedImages.length !== imagesInBase64.length) {
      const resizedImages = await resizeAndCompressImages(imagesInBase64);
      const rotatedImages = await rotateAllImagesToVertical(resizedImages);
      processedImages = await cropImage(rotatedImages);
      setCompressedImages(processedImages);
    }

    setImagesPickerVisible(false);

    setHitRadius(parseFloat(selectedCaliber) / 2);
    setTargetScannerRendered(true);

    localStorage.setItem(SHOTS_PER_TARGET_LOCAL_STORAGE_KEY, shotsPerTarget);
    localStorage.setItem(TEST_SHOTS_PER_TARGET_LOCAL_STORAGE_KEY, testShotsPerTarget);
  };

  const returnToSessionData = async (confirmDiscard: boolean = false): Promise<boolean> => {
    if (confirmDiscard) {
      const proceed = await confirm({
        message: "Powrót do ustawień sesji rozpocznie skanowanie tarcz od początku. Czy chcesz kontynuować?",
        confirmLabel: "Tak, edytuj ustawienia",
        cancelLabel: "Nie, anuluj",
        confirmColor: "#dc3545"
      });

      if (!proceed) {
        return false;
      }
    }

    setTargetResults([]);
    setSessionSaved(false);
    setTargetScannerRendered(false);
    setImagesPickerVisible(true);
    return true;
  };

  const returnToLastTarget = () => {
    setTargetResults((currentResults) => currentResults.slice(0, -1));
    setSessionSaved(false);
    setTargetScannerRendered(true);
  };

  const shotsPerTargetInputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.validity.patternMismatch) {
      setShotsPerTarget(e.target.value);
    } else {
      console.log("ERROR");
    }
  }

  const testShotsPerTargetInputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.validity.patternMismatch) {
      setTestShotsPerTarget(e.target.value);
    } else {
      console.log("ERROR");
    }
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const handleOwnerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOwner(e.target.value);
  };

  const handleClubChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clubWasEditedRef.current = true;
    setClub(e.target.value);
  };

  const saveSession = async () => { 
    try {
      const payload = {
        reporterId: authContext.getAccountId(),
        shootingEventId: shootingEventConfig.shootingEventId,
        sessionDate: selectedDate,
        ...(competitionMode ? { owner: owner, club: club } : {}),
        hitRadius: hitRadius,
        shotsPerTarget: getShotsPerTarget(),
        targetResults: targetResults,
        ...(competitionId ? { competitionId } : {})
      };

      await apiFetch(`${backendURL}/shooting-sessions`, {
        method: "POST",
        body: JSON.stringify(payload),
        includeAuth: true
      });

        success("Sesja zapisana pomyślnie");
        setSessionSaved(true);
        skipUnsavedSessionReportPromptRef.current = true;
        navigate(-1);
      
    } catch (err) {
      console.error("Failed to save session: ", err);
    }
  };

  const getShotsPerTarget = (): number => {
    return parseInt(shotsPerTarget) - (competitionMode ? parseInt(testShotsPerTarget) : 0);
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  return (
    <Container fluid id="mainContainer" className="py-3">
      {competitionMode && competitionName && competitionDate && !targetScannerRendered && targetResults.length == 0 && (
        <>
          <h1 className="font-weight-light text-center mb-1">
            {competitionName} {formatDate(competitionDate)}
          </h1>
          <h3 className="font-weight-light text-center mb-2">{shootingEventConfig.shootingEventName}</h3>
        </>
      )}
      <Row>
        {!targetScannerRendered && targetResults.length == 0 ? (
          <Col>
            <Row className="mt-4">
              <Col md={6}>
                {!competitionMode &&
                  <div className="form-row">
                    <label htmlFor="shootingEventInput">Konkurencja:</label>
                    <select id="shootingEventInput" value={selectedShootingEventId} onChange={handleShootingEventChange}>
                      {shootingEventConfigs.map((config) => (
                        <option key={config.shootingEventId} value={config.shootingEventId}>
                          {config.shootingEventName}
                        </option>
                      ))}
                    </select>
                  </div>
                }
                <div className="form-row">
                  <label htmlFor="shotsPerTargetInput">Liczba strzałów na tarczę:</label>
                  <input type="text" inputMode="numeric" pattern="^([1-9]|[1-9][0-9])$" id="shotsPerTargetInput" onChange={shotsPerTargetInputOnChange} value={shotsPerTarget || ''} />
                </div>
                {competitionMode &&
                    <div className="form-row">
                      <label htmlFor="testShotsPerTargetInput">(w tym strzały próbne):</label>
                      <input type="text" inputMode="numeric" pattern="^([0-9]|[1-9][0-9])$" id="testShotsPerTargetInput" onChange={testShotsPerTargetInputOnChange} value={testShotsPerTarget || ''} />
                    </div>
                  }
                <div className="form-row">
                  <label htmlFor="caliberInput">Kaliber broni:</label>
                  <select id="caliberInput" value={selectedCaliber} onChange={e => setSelectedCaliber(e.target.value)}>
                    {shootingEventConfig.calibers.map((caliberOption) => (
                      <option key={caliberOption.value} value={caliberOption.value} label={caliberOption.label} />
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label htmlFor="dateInput">Data sesji: </label>
                  <input id="dateInput" type="date" value={selectedDate} onChange={handleDateChange} />
                </div>
              </Col>
              {competitionMode && <Col md={6}>
                <div className="form-row">
                  <label htmlFor="ownerInput">Nazwisko i imię:</label>
                  <input id="ownerInput" type="text" value={owner} onChange={handleOwnerChange} />
                </div>
                <div className="form-row">
                  <label htmlFor="clubInput">Klub strzelca:</label>
                  <input id="clubInput" type="text" value={club} onChange={handleClubChange} />
                </div>
              </Col>}
            </Row>
            <div className="d-flex justify-content-end">
              <Button variant="info" onClick={() => showInfo({ message: <NewSessionHelpContent /> })}>Pomoc</Button>
            </div>
            <TargetImageUploader
              withIcon={true}
              withPreview={true}
              defaultImages={images}
              onChange={onDrop}
              imgExtension={[".jpg", ".jpeg", ".png"]}
              maxFileSize={8242880}
              buttonText={'Wybierz zdjęcia tarcz...'}
              label={'akceptowane formaty plików: .jpg, .jpeg, .png'}
            />
            <Button id="nextButton" size="lg" disabled={!(images.length !== 0)} onClick={() => processImagesBeforeScanning(images)}>Dalej</Button>
          </Col>
        ) : null}
        {!imagesPickerVisible && <Col id="targetScanner" className={targetScannerRendered ? "" : "d-none"}>
          <TargetScanner
            targetImages={compressedImages}
            shotsPerTarget={parseInt(shotsPerTarget)}
            setTargetScannerRendered={setTargetScannerRendered}
            setTargetResults={setTargetResults}
            targetResults={targetResults}
            hitRadius={hitRadius} 
            testShotsPerTarget={competitionMode ? parseInt(testShotsPerTarget) : 0}
            competitionMode={competitionMode}
            shootingEventConfig={shootingEventConfig}
            onBackToSessionData={returnToSessionData}
            isActive={targetScannerRendered}
          />
        </Col>}


        {!targetScannerRendered && targetResults.length > 0 ? (
          <Col style={{ width: "100%" }}>
            <SessionReport
              targetResults={targetResults}
              dateString={selectedDate}
              shotsPerTarget={getShotsPerTarget()}
              hitRadius={hitRadius}
              targetOwnerName={competitionMode ? owner : undefined}
              competitionName={competitionName}
              competitionDate={competitionDate}
              actionButtons={(disabled) => (
                <>
                  <div className="session-report-navigation-actions">
                    <Button disabled={disabled} variant="secondary" onClick={returnToLastTarget}>Wróć do ostatniej tarczy</Button>
                    <Button disabled={disabled} variant="outline-secondary" onClick={() => returnToSessionData(true)}>Edytuj ustawienia sesji</Button>
                  </div>
                  <Button disabled={disabled} id="saveSessionButton" onClick={saveSession}>Zapisz sesję</Button>
                </>
              )}
              shootingEventConfig={shootingEventConfig}
            />
          </Col>
        ) : null}
      </Row>
    </Container>
  );
}

function useUnsavedSessionReportPrompt(
  shouldBlock: boolean,
  skipPromptRef: React.MutableRefObject<boolean>,
  confirm: (options: {
    message: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmColor?: string;
  }) => Promise<boolean>,
  registerNavigationGuard: (guard: () => Promise<boolean> | boolean) => () => void
) {
  const promptVisibleRef = useRef<boolean>(false);

  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldBlock]);

  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    return registerNavigationGuard(async () => {
      if (skipPromptRef.current) {
        return true;
      }

      if (promptVisibleRef.current) {
        return false;
      }

      promptVisibleRef.current = true;

      try {
        return await confirm({
          message: "Czy chcesz opuścić stronę bez zapisania wyników? Aby zachować wyniki, kliknij „Zapisz sesję”.",
          confirmLabel: "Opuść bez zapisu",
          cancelLabel: "Wróć do raportu",
          confirmColor: "#dc3545"
        });
      } finally {
        promptVisibleRef.current = false;
      }
    });
  }, [confirm, registerNavigationGuard, shouldBlock, skipPromptRef]);
}

const infoMessage = `Wprowadź zdjęcia lub skany tarcz ze swojego treningu w wybranej konkurencji. 

WAŻNE: Zdjęcie tarczy powinno zostać zrobione w ORIENTACJI PIONOWEJ. 

Tarcza powinna być skierowana jak najbardziej PROSTOPADLE do obiektywu, a przestrzeliny powinny wyraźnie kontrastować. 

Zdjęcie najlepiej wykonać na białym, jasnym tle (można np. podłożyć pod tarczę białą kartkę papieru lub zrobić zdjęcie na tle jasnego światła, np. nieba, sztucznego oświetlenia). Bardzo ważne jest również dobre oświetlenie z frontu tarczy, tak aby nie powstawały bardzo wyraźne cienie i refleksy. W ciemnych pomieszczeniach warto użyć lampy błyskowej.

Przykłady dobrych zdjęć:`;

const infoMessage2 = `Nie stosowanie się do powyższych porad może skutkować pogorszonym działaniem automatycznej detekcji czarnego pola oraz trafień.`

const helpImages = [
  {
    src: helpTarget1,
    alt: "Przykład zdjęcia tarczy leżącej płasko na jasnym podłożu",
    caption: "Biała kartka jako tło"
  },
  {
    src: helpTarget2,
    alt: "Przykład zdjęcia tarczy wykonany z bliska przy dobrym kontraście przestrzelin",
    caption: "Okno jako tło"
  },
  {
    src: helpTarget3,
    alt: "Przykład zdjęcia tarczy z jasnym tłem i widocznym czarnym polem",
    caption: "Tył drugiej tarczy jako tło"
  }
];

function NewSessionHelpContent() {
  return (
    <div className="new-session-help-dialog">
      <p>{infoMessage}</p>
      <div className="new-session-help-examples" aria-label="Przykładowe zdjęcia tarczy">
        {helpImages.map((image) => (
          <figure key={image.src} className="new-session-help-example">
            <img src={image.src} alt={image.alt} />
            <figcaption>{image.caption}</figcaption>
          </figure>
        ))}
      </div>
      <p>{infoMessage2}</p>
    </div>
  );
}
